<script setup lang="ts">
import { type CheckboxValueType, ElCheckbox } from 'element-plus'

const props = withDefaults(defineProps<{ size?: 'default' | 'small' | 'large'; value?: string | boolean | number }>(), {
  size: 'default',
})
defineEmits(['change'])
const modelValue = defineModel<boolean>('modelValue')
defineSlots<{ default(): void }>()
</script>
<template>
  <ElCheckbox v-bind="props" v-model="modelValue" @change="(val: CheckboxValueType) => $emit('change', val)">
    <slot name="default" />
  </ElCheckbox>
</template>
<style lang="scss" scoped>
.el-checkbox {
  --el-checkbox-input-width: 18px;
  --el-checkbox-input-height: 18px;

  --el-checkbox-input-border: none;
  --el-checkbox-border-radius: 2px;

  --el-checkbox-bg-color: transparent;
  --el-checkbox-checked-bg-color: transparent;
  --el-checkbox-disabled-input-fill: transparent;
  --el-checkbox-disabled-checked-input-fill: transparent;

  --el-checkbox-input-border-color-hover: var(--primary);
  --el-checkbox-text-color: var(--primary);
  --el-checkbox-checked-text-color: var(--primary);
}

/*
 * Element Plus builds the box out of a border, a background and a CSS tick. The design
 * ships one icon per state instead, so the box is painted in --primary and masked to the
 * icon exported from Figma. The tick is a hole in the checked icon, exactly as designed.
 */
:deep(.el-checkbox__inner::after) {
  all: unset;
  content: '';
  position: absolute;
  inset: 0;
  background-color: var(--primary);
  mask: url('../../assets/icons/checkbox-unchecked.svg') center / 18px 18px no-repeat;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner::after) {
  transform: none;
  mask-image: url('../../assets/icons/checkbox-checked.svg');
}

:deep(.el-checkbox__input.is-disabled .el-checkbox__inner::after) {
  background-color: var(--el-disabled-text-color);
}

.el-checkbox.el-checkbox--large,
.el-checkbox.el-checkbox--small {
  :deep(.el-checkbox__inner) {
    width: var(--el-checkbox-input-width);
    height: var(--el-checkbox-input-height);
  }

  :deep(.el-checkbox__inner::after) {
    width: auto;
    height: auto;
  }
}

:deep(.el-checkbox__label) {
  font-size: 16px;
  line-height: 24px;
}
</style>
